export interface ClientDashboardSelection {
	selectedDate: number;
	manualRange: { from: string; to: string } | null;
	currency: string;
}

type Dashboard = { scope: string; clientId: number; selection: ClientDashboardSelection };
let active: Dashboard | undefined;
let pending: (Dashboard & { scroll: ReturnType<typeof holdScroll>; timeout: number }) | undefined;

// Only the mounted client Overview registers itself. Other tabs and dashboards
// keep their own filters; this is a handoff, not a persistent global preference.
export function registerClientDashboard(dashboard: Dashboard) {
	active = dashboard;
	return () => { if (active === dashboard) active = undefined; };
}

export function getClientDashboardSelection(scope: string, clientId: number) {
	return pending?.scope === scope && pending.clientId === clientId ? pending.selection : undefined;
}

export function prepareClientDashboardComparison(scope: string, fromId: number, toId: number) {
	if (fromId === toId) return;
	const source = active?.scope === scope && active.clientId === fromId ? active
		: pending?.scope === scope && pending.clientId === fromId ? pending : undefined;
	const selection = source && { ...source.selection, manualRange: source.selection.manualRange && { ...source.selection.manualRange } };
	const position = pending?.scroll.position ?? { x: window.scrollX, y: window.scrollY };
	clearPending();
	if (!selection) return;
	const scroll = holdScroll(position);
	pending = { scope, clientId: toId, selection, scroll,
		timeout: window.setTimeout(scroll.dispose, 30_000) };
}

export function finishClientDashboardComparison(scope: string, clientId: number) {
	if (pending?.scope !== scope || pending.clientId !== clientId) return;
	const transfer = pending;
	// Wait for the new statistics (or error state) to be committed and measured.
	requestAnimationFrame(() => requestAnimationFrame(() => {
		if (pending !== transfer) return;
		transfer.scroll.restore();
		clearPending();
	}));
}

export function clearClientDashboardComparison() {
	active = undefined;
	clearPending();
}

export function cancelFailedClientDashboardComparison(clientId: number) {
	if (pending?.clientId === clientId) clearPending();
}

function clearPending() {
	if (!pending) return;
	window.clearTimeout(pending.timeout);
	pending.scroll.dispose();
	pending = undefined;
}

function holdScroll(saved: { x: number; y: number }) {
	const body = document.body;
	const previousHeight = body.style.getPropertyValue("min-height");
	const previousPriority = body.style.getPropertyPriority("min-height");
	// Auxiliary widgets can shrink while reloading. Reserve the document height
	// until the new dashboard is ready so the browser cannot clamp its scroll.
	body.style.setProperty("min-height", `${Math.max(body.scrollHeight, document.documentElement.scrollHeight, saved.y + window.innerHeight)}px`);
	window.scrollTo({ left: saved.x, top: saved.y, behavior: "instant" });
	let held = true;
	const releaseHeight = () => {
		if (previousHeight) body.style.setProperty("min-height", previousHeight, previousPriority);
		else body.style.removeProperty("min-height");
	};
	const dispose = () => {
		if (!held) return;
		held = false;
		releaseHeight();
		for (const event of ["wheel", "touchstart", "pointerdown"]) window.removeEventListener(event, dispose, true);
		window.removeEventListener("keydown", onKey, true);
	};
	const onKey = (event: KeyboardEvent) => {
		if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab"].includes(event.key)) dispose();
	};
	for (const event of ["wheel", "touchstart", "pointerdown"]) window.addEventListener(event, dispose, { capture: true, passive: true });
	window.addEventListener("keydown", onKey, true);
	return {
		get position() { return held ? saved : { x: window.scrollX, y: window.scrollY }; },
		dispose,
		restore: () => {
			if (!held) return; // Never override scrolling or interaction during loading.
			dispose();
			window.scrollTo({ left: saved.x, top: saved.y, behavior: "instant" });
		},
	};
}
